import { createAuthClient } from 'better-auth/react';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Better Auth client configured for cross-domain Bearer token auth.
 *
 * The frontend (*.pages.dev) and API (*.workers.dev) are on completely
 * different domains. Third-party cookies (SameSite=None) are blocked
 * in incognito mode and increasingly in regular browsing due to
 * Chrome's cookie deprecation. Instead of relying on cookies, we use
 * the Better Auth Bearer plugin:
 *
 * - On sign-in/sign-up success, the API returns the session token in a
 *   `set-auth-token` response header.
 * - We store it in localStorage and send it as `Authorization: Bearer <token>`
 *   on all subsequent requests.
 * - The server's Bearer plugin validates this token exactly like it
 *   would validate a cookie-based session.
 *
 * This approach works regardless of third-party cookie policies.
 */
export const authClient = createAuthClient({
  baseURL: API_BASE || window.location.origin,
  fetchOptions: {
    credentials: 'include', // kept as fallback for same-domain scenarios
    auth: {
      type: 'Bearer',
      token: () => localStorage.getItem('bearer_token') || '',
    },
    onSuccess: (ctx) => {
      const authToken = ctx.response.headers.get('set-auth-token');
      if (authToken) {
        localStorage.setItem('bearer_token', authToken);
      }
    },
  },
});

// Re-exported for convenience so components import one module for auth state.
export const { useSession, signIn, signOut, signUp } = authClient;

/**
 * Call this on sign-out to clear the stored Bearer token.
 */
export function clearAuthToken() {
  localStorage.removeItem('bearer_token');
}
